<template>
  <div class="prompt-panel">
    <button type="button" class="prompt-panel__toggle" @click="toggle" :aria-expanded="String(expanded)">
      <v-icon size="16" class="prompt-panel__toggle-icon">
        mdi-auto-fix
      </v-icon>
      <span class="prompt-panel__toggle-text">{{ expanded ? 'Close' : 'Prompt' }}</span>
      <v-icon size="18" class="prompt-panel__toggle-chevron">
        {{ expanded ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
      </v-icon>
    </button>

    <transition name="prompt-panel-slide">
      <div v-if="expanded" class="prompt-panel__content" role="group" aria-label="Prompt templates">
        <button
          type="button"
          class="prompt-panel__nav-btn"
          aria-label="Scroll templates left"
          :disabled="!canScrollLeft"
          :aria-disabled="String(!canScrollLeft)"
          @click="scrollLeft"
        >
          <v-icon size="18">mdi-chevron-left</v-icon>
        </button>

        <div ref="list" class="prompt-panel__list" @scroll.passive="syncScrollState">
          <v-menu
            v-for="(pt, index) in promptTemplates"
            :key="index"
            top
            open-on-hover
            :open-delay="60"
            :close-delay="120"
            :close-on-content-click="false"
            :offset-y="true"
            content-class="prompt-template-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <button class="prompt-panel__btn" v-bind="attrs" v-on="on" @click="$emit('apply-prompt-template', pt)">
                <v-icon v-if="pt.icon" left size="18">{{ pt.icon }}</v-icon>
                {{ pt.label }}
              </button>
            </template>
            <div class="prompt-template-tooltip__content" style="color: #fff">
              {{ String(pt.prompt || '').trim() }}
            </div>
          </v-menu>
        </div>

        <button
          type="button"
          class="prompt-panel__nav-btn"
          aria-label="Scroll templates right"
          :disabled="!canScrollRight"
          :aria-disabled="String(!canScrollRight)"
          @click="scrollRight"
        >
          <v-icon size="18">mdi-chevron-right</v-icon>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { PROMPT_TEMPLATES } from './utils/prompt-templates';
import event from '@/plugins/event-bus';

export default {
  name: 'PromptTemplatesPanel',
  props: {
    product: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      expanded: true,
      canScrollLeft: false,
      canScrollRight: false,
      chatbotScrollHandler: null,
      listResizeObserver: null,
      scrollStatePollTimer: null,
    };
  },
  computed: {
    promptTemplates() {
      const productUrl = this.getProductUrl(this.product) || '';
      return PROMPT_TEMPLATES.map(pt => ({
        ...pt,
        prompt: this.applyTemplateVars(pt.prompt, { product_url: productUrl }),
      }));
    },
  },
  watch: {
    expanded() {
      this.$nextTick(() => {
        this.syncScrollState();
        this.startScrollStatePolling();
      });
    },
    product: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.syncScrollState();
          this.startScrollStatePolling();
        });
      },
    },
  },
  methods: {
    applyTemplateVars(prompt, vars) {
      const input = String(prompt || '');
      const productUrl = String(vars?.product_url || '');
      return input.replace(/\{\{\s*product_url\s*\}\}/g, productUrl);
    },
    collapse() {
      if (this.expanded) this.expanded = false;
    },
    toggle() {
      this.expanded = !this.expanded;
    },
    getScrollEl() {
      return this.$refs.list;
    },
    syncScrollState() {
      const el = this.getScrollEl();
      if (!el) return;

      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const left = Math.max(0, el.scrollLeft);
      const epsilon = 2;

      this.canScrollLeft = left > epsilon;
      this.canScrollRight = left < maxScrollLeft - epsilon;
    },
    startScrollStatePolling() {
      if (!this.expanded) return;
      if (this.scrollStatePollTimer) window.clearInterval(this.scrollStatePollTimer);

      const startedAt = Date.now();
      const maxMs = 1200;
      let last = null;

      this.scrollStatePollTimer = window.setInterval(() => {
        const el = this.getScrollEl();
        if (!el) return;

        const snap = `${el.clientWidth}:${el.scrollWidth}:${el.scrollLeft}`;
        if (snap !== last) {
          last = snap;
          this.syncScrollState();
        }

        if (Date.now() - startedAt > maxMs) {
          window.clearInterval(this.scrollStatePollTimer);
          this.scrollStatePollTimer = null;
        }
      }, 80);
    },
    scrollByAmount(dir) {
      const el = this.getScrollEl();
      if (!el) return;
      const amount = Math.max(120, Math.floor(el.clientWidth * 0.7));
      el.scrollBy({ left: dir * amount, behavior: 'smooth' });
      window.setTimeout(() => this.syncScrollState(), 200);
    },
    scrollLeft() {
      this.scrollByAmount(-1);
    },
    scrollRight() {
      this.scrollByAmount(1);
    },
    getProductUrl(item) {
      const handle = item?.metafieldsGlobalUrl || item?.handle || '';

      let sid = '';
      try {
        const dropnoxeUser = JSON.parse(localStorage.getItem('dropnoxe-user') || '{}');
        sid = dropnoxeUser?.id || '';
      } catch (e) {
        sid = '';
      }
      const sidQuery = sid ? `?plf_sid=${encodeURIComponent(sid)}` : '';

      if (this.$store.state.auth?.accountInfo?.rDomain?.includes('.dropnoxe.com')) {
        return item.gemPage && item.gemPage.titleButton && item.gemPage.title
          ? `https://${this.$store.state.auth.accountInfo.rDomain}/posts/${handle}-news${sidQuery}`
          : `https://${this.$store.state.auth.accountInfo.rDomain}/products/${handle}${sidQuery}`;
      } else {
        return item.gemPage && item.gemPage.titleButton && item.gemPage.title
          ? `https://www.${this.$store.state.auth.accountInfo.rDomain}/posts/${handle}-news${sidQuery}`
          : `https://www.${this.$store.state.auth.accountInfo.rDomain}/products/${handle}${sidQuery}`;
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.syncScrollState();

      const el = this.getScrollEl();
      if (el && typeof window !== 'undefined' && 'ResizeObserver' in window) {
        this.listResizeObserver = new window.ResizeObserver(() => this.syncScrollState());
        this.listResizeObserver.observe(el);
      }

      // If this mounts while hidden (width=0), sync again on next paint(s)
      window.requestAnimationFrame(() => this.syncScrollState());
      window.requestAnimationFrame(() => this.syncScrollState());
      this.startScrollStatePolling();
    });
    window.addEventListener('resize', this.syncScrollState, { passive: true });

    this.chatbotScrollHandler = () => this.collapse();
    event.$on('chatbot:scroll', this.chatbotScrollHandler);
  },
  activated() {
    this.$nextTick(() => {
      this.syncScrollState();
      this.startScrollStatePolling();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncScrollState);
    if (this.listResizeObserver) {
      this.listResizeObserver.disconnect();
      this.listResizeObserver = null;
    }
    if (this.scrollStatePollTimer) {
      window.clearInterval(this.scrollStatePollTimer);
      this.scrollStatePollTimer = null;
    }
    if (this.chatbotScrollHandler) {
      event.$off('chatbot:scroll', this.chatbotScrollHandler);
      this.chatbotScrollHandler = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.prompt-panel {
  position: absolute;
  top: -46px;
  z-index: 1;
  display: inline-flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  padding-right: 12px;
  filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.08));
}

.prompt-panel__toggle,
.prompt-panel__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--v-border-base);
  background: #ffffff;
  backdrop-filter: blur(6px);
  cursor: pointer;
  white-space: nowrap;
  font-size: 13px !important;
  font-weight: 500;
  gap: 0 !important;
  transition: all 0.3s ease;

  &:hover {
    background: var(--main-background);
  }

  &-icon {
    margin-right: 6px;
  }
}

.prompt-panel__content {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  overflow: hidden;
  flex: 1;

  justify-content: flex-end;
}

.prompt-panel__list {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  /* flex: 1 1 auto; */
  min-width: 0;

  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
}

.prompt-panel__nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid var(--v-border-base);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  cursor: pointer;
  flex: 0 0 auto;
  z-index: 10;

  &:hover {
    background: #e4e3e3;
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
    pointer-events: none;
    &:hover {
      background: rgba(255, 255, 255, 0.95);
    }
  }
}
</style>

<style lang="scss">
.prompt-template-tooltip {
  max-width: 300px;
  width: 100%;
  max-height: 400px;
  height: auto;
  padding: 12px 14px;
  overflow: auto;
  background: rgba(0, 0, 0, 0.92);
  transform: translateY(-8px);
  border-radius: 8px;
}

.prompt-template-tooltip__content {
  white-space: pre-line;
  word-break: break-word;
}
</style>
